'use client';

import { useSearchParams } from 'next/navigation';
import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import type { Tournament } from '@/api/cms/tournaments';
import { BonusStage } from '@/api/common';
import type { GameV1 } from '@/api/games';
import { AuthButtons } from '@/components/general/TabBar/AuthButtons';
import { IconComponent } from '@/components/IconComponent';
import { capitalizeWords } from '@/helpers/common';
import { CustomerIOEvents } from '@/helpers/customerio';
import { timestamp } from '@/helpers/dateTime';
import { Modals } from '@/helpers/modals';
import { DEMO_CURRENCY } from '@/helpers/variables';
import { useCustomerIO } from '@/hooks/3dParty/customerIO';
import { useToggle } from '@/hooks/common';
import useEstonia from '@/hooks/estonia';
import { useGameOpenFlow } from '@/hooks/game';
import { useNDBFlow } from '@/hooks/useNDBFlow';
import { useScrollBlock } from '@/modules/common/hooks/useScrollBlock';
import { useGetCommonStore, useModalsStore } from '@/store';
import { REASON_FOR_UPDATING_USER } from '@/store/commonStore/slices/auth/helpers';
import { ScContainer } from '@/styles/common';
import { useTournaments } from '../common/hooks/useTournaments';
import { useMatchingSections } from '../restrictions/hooks/useAccessSections';
import { ActionsPanel } from './ActionsPanel';
import { BottomGamesSlider } from './BottomGamesSlider';
import { GameBlock } from './GameBlock';
import { ProviderGamesSection } from './ProviderGamesSection';
import { RecommendationsModal } from './RecommendationsModal';
import {
  ScGameCard,
  ScGameCardFooter,
  ScGameContainer,
  ScGamePage,
  ScGamePageContainer,
  ScGameTitle,
} from './styled';
import { TournamentsWidget } from './TournamentsWidget';

interface GameCardProps {
  game?: GameV1;
  isGeoBlocked?: boolean;
}

export const GameCard = ({
  game,
  isGeoBlocked,
}: PropsWithChildren<GameCardProps>) => {
  const searchParams = useSearchParams();
  const { t } = useTranslation('common');
  const { isEstonia } = useEstonia();
  const open = useModalsStore.use.open();
  const useCommonStore = useGetCommonStore();

  const {
    isAuthenticated,
    user,
    userTournamentsStatuses,
    fetchUser,
    freespins,
    isMobileDevice,
    activeTournaments: tournaments,
  } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      userTournamentsStatuses: state.userTournamentsStatuses,
      fetchUser: state.fetchUser,
      freespins: state.freespins,
      isMobileDevice: state.isMobileDevice,
      activeTournaments: state.activeTournaments,
    }))
  );

  const { isNDBFlow, localNDBFlow, isAfterReleaseDate, isNoDepositUser } =
    useNDBFlow();

  const hasFinishedFS = useMemo(() => {
    if (!freespins) return false;
    return freespins.some(({ stage }) => stage === BonusStage.Finished);
  }, [freespins]);

  useEffect(() => {
    if (
      hasFinishedFS &&
      isNDBFlow &&
      isNoDepositUser &&
      !localNDBFlow?.isNDBPlayForRealShown &&
      isAfterReleaseDate
    ) {
      open(Modals.NDBPlayForReal);
    }
  }, [hasFinishedFS, isNDBFlow, open, isAfterReleaseDate, isNoDepositUser]);

  const modal = useModalsStore.use.modal();

  const isDemoParam = !!searchParams.get('demo');
  const [isDemo, setIsDemo] = useState(isDemoParam);

  const isUnloggedMobileCard = isMobileDevice && !isAuthenticated && !isDemo;

  const isMobileAllowed = isMobileDevice && !isGeoBlocked;

  const [isFullScreen, toggleFullScreen, setFullScreen] = useToggle(
    isMobileAllowed && !isUnloggedMobileCard
  );

  useEffect(() => {
    if (isMobileAllowed) {
      setFullScreen(!isUnloggedMobileCard);
    }
  }, [isMobileAllowed, isUnloggedMobileCard, setFullScreen]);

  const { trackCustomEvent } = useCustomerIO();
  const { closeGameFlow } = useGameOpenFlow();

  const { data: tournamentsB } = useTournaments();

  const isDemoAvailable = useMemo(
    () => !!game?.currencies[DEMO_CURRENCY],
    [game?.currencies]
  );

  useEffect(() => {
    if (modal) return;
    setIsDemo(isDemoParam);
  }, [modal, isDemoParam]);

  useEffect(
    () => () => {
      fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
    },
    [fetchUser]
  );

  useScrollBlock(!isFullScreen);

  useEffect(
    () => () => {
      trackCustomEvent(CustomerIOEvents.GAME_CLOSED);
    },
    [trackCustomEvent]
  );

  const activeFilteredTournaments = useMatchingSections(tournaments);

  const gameTournaments: Tournament[] = useMemo(() => {
    if (!(tournaments && user && game?.listCollections?.length)) return [];

    return activeFilteredTournaments
      .filter(
        ({
          backendTournamentId,
          collectionName,
          banner: { durationFrom, durationTo },
        }) => {
          const currentTimestamp = timestamp();

          const durationFromTimestamp = timestamp(durationFrom);
          const durationToTimestamp = timestamp(durationTo);

          return (
            backendTournamentId &&
            game.listCollections.includes(collectionName) &&
            ((durationFromTimestamp < currentTimestamp &&
              durationToTimestamp > currentTimestamp) ||
              userTournamentsStatuses?.find(
                ({ tournamentId }) =>
                  tournamentId === Number(backendTournamentId)
              ))
          );
        }
      )
      .map((tournament) => ({
        ...tournament,
        backendTournament: tournamentsB?.find(
          ({ id }) => id === Number(tournament?.backendTournamentId)
        ),
      }));
  }, [
    tournaments,
    user,
    game?.listCollections,
    userTournamentsStatuses,
    tournamentsB,
  ]);

  const visibleGameTitle =
    isFullScreen || isUnloggedMobileCard || !game?.title
      ? undefined
      : capitalizeWords(game.title);

  return (
    <>
      <ScGamePageContainer
        isAuthenticated={isAuthenticated}
        isEstonia={isEstonia}
        isFullScreen={isFullScreen}
        isMobile={isMobileDevice}
        isUnloggedCard={isUnloggedMobileCard}
      >
        <div className="bg" />

        {visibleGameTitle && (
          <ScGameTitle>
            <button
              aria-label={t('auth.registration.buttons.back')}
              className="back"
              onClick={closeGameFlow}
              type="button"
            >
              <IconComponent name="whiteArrowRight" size="24px" />
            </button>
            <h1 className="title">{visibleGameTitle}</h1>
          </ScGameTitle>
        )}
        <ScGamePage isFullScreen={isFullScreen} isMobile={isMobileDevice}>
          <ScGameCard
            isAuthenticated={isAuthenticated}
            isFullScreen={isFullScreen}
            isMobile={isMobileDevice}
          >
            <ScGameContainer
              isAddons
              isFullScreen={isFullScreen}
              isMobile={isMobileDevice}
              withTournamentBanner={isMobileDevice && !!gameTournaments.length}
            >
              {isMobileDevice && (
                <TournamentsWidget gameTournaments={gameTournaments} />
              )}
              <GameBlock
                game={game}
                isDemo={isDemo}
                isDemoAvailable={isDemoAvailable}
                isGeoBlocked={isGeoBlocked}
                isMobile={isMobileDevice}
              />
            </ScGameContainer>
            {game && !isUnloggedMobileCard && (
              <ActionsPanel
                currencies={game.real}
                gameProvider={game.provider || ''}
                gameTitle={capitalizeWords(game.title)}
                isDemo={isDemo}
                isDemoAvailable={isDemoAvailable}
                isFullScreen={isFullScreen}
                isMobile={isMobileDevice}
                onFullScreen={toggleFullScreen}
                withTournamentBanner={!!gameTournaments.length}
              />
            )}
            {!isAuthenticated && isMobileDevice && (
              <ScGameCardFooter>
                <AuthButtons fullSizeAddons={false} />
              </ScGameCardFooter>
            )}
          </ScGameCard>
        </ScGamePage>
      </ScGamePageContainer>
      <ScContainer>
        {!(isMobileDevice || isFullScreen) && (
          <RecommendationsModal gameTournaments={gameTournaments} />
        )}

        {isGeoBlocked && <BottomGamesSlider />}
      </ScContainer>

      {game && !isGeoBlocked && (
        <ProviderGamesSection providerId={game.provider} />
      )}
    </>
  );
};
